import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  inject,
  signal,
  ViewChild,
  ElementRef,
  OnDestroy,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { forkJoin, from, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { UsersService } from '../../services/users/users.service';
import { StoreService, StoreOption } from '../../services/stores/store.service';
import { supabase } from '../../core/supabase/supabase.client';
import { Chart, registerables } from 'chart.js';
import { FormsModule } from '@angular/forms';
import { RealtimeChannel } from '@supabase/supabase-js';
import { AuthService } from '../../services/auth/auth.service';
import { DashboardService } from '../../services/dashboard/dashboard.service';
import {
  formatIndianCompactCurrency,
  formatIndianCompactNumber,
  parseIndianCompactNumber,
} from '../../utils/indian-compact-number';
import { IndianCompactPipe } from '../../pipes/indian-compact.pipe';

Chart.register(...registerables);
interface DashboardMetric {
  label: string;
  value: string;
  hint: string;
  badge: string;
  badgeClass: string;
  icon: string;
  iconClass: string;
  trend?: string;
  trendUp?: boolean;
}

interface DashboardOrder {
  id: string | number;
  payable_amount: number;
  status: string;
  created_at: string;
  user?: { name: string };
  store?: { store_name: string };
}

interface DashboardStatusItem {
  label: string;
  value: number;
  icon: string;
  colorClass: string;
  progressClass: string;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, IndianCompactPipe],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  readonly #usersService = inject(UsersService);
  readonly #storeService = inject(StoreService);
  readonly #authService = inject(AuthService);
  readonly #dashboardService = inject(DashboardService);

  loading = signal<boolean>(true);
  error = signal<string>('');
  lastUpdated = signal<Date>(new Date());
  currentTime = signal<Date>(new Date());
  private clockInterval: ReturnType<typeof setInterval> | null = null;

  stores = signal<StoreOption[]>([]);
  selectedStoreId = signal<number | null>(null);
  isStoreManager = signal<boolean>(false);
  chartData = signal<{ labels: string[]; values: number[] }>({
    labels: [],
    values: [],
  });

  topMetrics = signal<DashboardMetric[]>([]);
  recentOrders = signal<DashboardOrder[]>([]);
  todayStatus = signal<DashboardStatusItem[]>([]);
  weeklySales = signal(0);
  averageOrderValue = signal(0);

  @ViewChild('dashboardChart') dashboardChart!: ElementRef<HTMLCanvasElement>;
  private chartInstance: Chart | null = null;
  private realtimeChannel: RealtimeChannel | null = null;
  private realtimeRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    const role = this.#authService.role();
    const assignedStoreId = this.#authService.storeId();

    if (role === 'store_manager' && assignedStoreId) {
      this.isStoreManager.set(true);
      this.selectedStoreId.set(Number(assignedStoreId));
    }

    this.#storeService.allStores().subscribe({
      next: (data) => {
        if (this.isStoreManager()) {
          this.stores.set(data.filter((s) => s.id === Number(assignedStoreId)));
        } else {
          this.stores.set(data);
        }
      },
      error: () => console.error('Failed to load stores'),
    });

    this.loadDashboard();
    this.setupRealtimeSubscription();
    this.startClock();
  }

  ngOnDestroy(): void {
    if (this.realtimeChannel) {
      supabase.removeChannel(this.realtimeChannel);
    }
    if (this.clockInterval) {
      clearInterval(this.clockInterval);
    }
    if (this.realtimeRefreshTimer) {
      clearTimeout(this.realtimeRefreshTimer);
    }
  }

  private startClock(): void {
    this.clockInterval = setInterval(() => {
      this.currentTime.set(new Date());
    }, 1000);
  }

  onStoreChange(val: any): void {
    this.selectedStoreId.set(
      val === 'null' || val === null ? null : Number(val)
    );
    this.loadDashboard();
    this.setupRealtimeSubscription();
  }

  get selectedStoreName(): string {
    const store = this.stores().find((s) => s.id === this.selectedStoreId());
    return store ? store.name : 'All Stores';
  }

  loadDashboard(): void {
    this.loading.set(true);
    this.error.set('');

    this.#dashboardService.getSummary(this.selectedStoreId()).subscribe({
      next: (summary) => {
        const orderCount = Number(summary.orders_today || 0);
        const salesToday = Number(summary.sales_today || 0);
        const ridersOnline = Number(summary.riders_online || 0);
        const customers = Number(summary.customers || 0);
        const status = summary.status || {
          new_orders: 0,
          preparing: 0,
          delivering: 0,
          delivered: 0,
          cancelled: 0,
        };

        this.todayStatus.set([
          {
            label: 'New orders',
            value: Number(status.new_orders || 0),
            icon: 'fa-solid fa-receipt',
            colorClass: 'text-warning bg-warning/10',
            progressClass: 'bg-warning',
          },
          {
            label: 'Being prepared',
            value: Number(status.preparing || 0),
            icon: 'fa-solid fa-box',
            colorClass: 'text-info bg-info/10',
            progressClass: 'bg-info',
          },
          {
            label: 'Out for delivery',
            value: Number(status.delivering || 0),
            icon: 'fa-solid fa-motorcycle',
            colorClass: 'text-primary bg-primary/10',
            progressClass: 'bg-primary',
          },
          {
            label: 'Delivered',
            value: Number(status.delivered || 0),
            icon: 'fa-solid fa-circle-check',
            colorClass: 'text-success bg-success/10',
            progressClass: 'bg-success',
          },
        ]);

        this.topMetrics.set([
          {
            label: 'Orders Today',
            value: formatIndianCompactNumber(orderCount),
            hint: `Volume for ${this.selectedStoreName}`,
            badge: 'Live',
            badgeClass: 'badge-success badge-outline',
            icon: 'fa-solid fa-bag-shopping',
            iconClass: 'bg-primary/10 text-primary',
          },
          {
            label: 'Sales Today',
            value: formatIndianCompactCurrency(salesToday),
            hint: `Revenue from ${this.selectedStoreName}`,
            badge: 'Live',
            badgeClass: 'badge-success badge-outline',
            icon: 'fa-solid fa-indian-rupee-sign',
            iconClass: 'bg-secondary/10 text-secondary',
          },
          {
            label: 'Riders Online',
            value: formatIndianCompactNumber(ridersOnline),
            hint: this.selectedStoreId()
              ? `Fleet for ${this.selectedStoreName}`
              : 'Available delivery partners',
            badge: 'Active',
            badgeClass: 'badge-info badge-outline',
            icon: 'fa-solid fa-motorcycle',
            iconClass: 'bg-info/10 text-info',
          },
          {
            label: 'Users',
            value: formatIndianCompactNumber(customers),
            hint: this.selectedStoreId()
              ? `Customers of ${this.selectedStoreName}`
              : 'Total registered customers',
            badge: 'Total',
            badgeClass: 'badge-ghost',
            icon: 'fa-solid fa-users',
            iconClass: 'bg-accent/10 text-accent',
          },
        ]);

        const chartRows = Array.isArray(summary.chart) ? summary.chart : [];
        const values = chartRows.map((row) => Number(row.sales || 0));
        this.chartData.set({
          labels: chartRows.map((row) => row.label),
          values,
        });
        this.weeklySales.set(values.reduce((total, value) => total + value, 0));
        this.averageOrderValue.set(
          Math.round(Number(summary.average_order_value || 0))
        );

        this.recentOrders.set(
          (summary.recent_orders || []).map((order) => ({
            id: order.id,
            payable_amount: Number(order.payable_amount || 0),
            status: order.status,
            created_at: order.created_at,
            user: { name: order.customer_name || 'Guest' },
            store: { store_name: order.store_name || this.selectedStoreName },
          }))
        );

        this.loading.set(false);
        this.lastUpdated.set(new Date(summary.generated_at || Date.now()));
        setTimeout(() => this.initChart(), 0);
      },
      error: () => {
        this.error.set('Failed to sync with command center.');
        this.loading.set(false);
      },
    });
  }

  private loadDashboardLegacy(): void {
    this.loading.set(true);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const last7Days = new Date(today);
    last7Days.setDate(last7Days.getDate() - 6);

    let ordersTodayQuery = supabase
      .from('orders')
      .select('payable_amount, status', { count: 'exact' })
      .gte('created_at', today.toISOString());
    let recentOrdersQuery = supabase
      .from('orders')
      .select('*, user:users(name), store:stores(store_name)')
      .order('created_at', { ascending: false })
      .limit(6);
    let chartQuery = supabase
      .from('orders')
      .select('payable_amount, created_at')
      .gte('created_at', last7Days.toISOString());

    const storeId = this.selectedStoreId();

    let ridersQuery = supabase
      .from('rider_presence')
      .select('rider_id, rider:riders!inner(store_id)', { count: 'exact' })
      .eq('is_online', true);

    if (storeId) {
      ordersTodayQuery = ordersTodayQuery.eq('store_id', storeId);
      recentOrdersQuery = recentOrdersQuery.eq('store_id', storeId);
      chartQuery = chartQuery.eq('store_id', storeId);
      ridersQuery = ridersQuery.eq('rider.store_id', storeId);
    }

    forkJoin({
      users: from(
        this.#usersService.getAllUsers(
          1,
          1,
          '',
          'name',
          'ASC',
          storeId || undefined,
          'user'
        )
      ).pipe(catchError(() => of({ total: 0 }))),
      ordersToday: from(ordersTodayQuery),
      riders: from(ridersQuery),
      recentOrders: from(recentOrdersQuery),
      chartOrdersResult: from(chartQuery),
    }).subscribe({
      next: ({
        users,
        ordersToday,
        riders,
        recentOrders,
        chartOrdersResult,
      }) => {
        const orderData =
          (ordersToday.data as { payable_amount: number; status: string }[]) ||
          [];
        const revenue = orderData.reduce(
          (acc, curr) => acc + (curr.payable_amount || 0),
          0
        );
        const statusCounts = orderData.reduce<Record<string, number>>(
          (counts, order) => {
            const status = order.status?.toLowerCase() || 'pending';
            counts[status] = (counts[status] || 0) + 1;
            return counts;
          },
          {}
        );
        const countStatuses = (...statuses: string[]) =>
          statuses.reduce(
            (total, status) => total + (statusCounts[status] || 0),
            0
          );

        this.todayStatus.set([
          {
            label: 'New orders',
            value: countStatuses('pending', 'confirmed'),
            icon: 'fa-solid fa-receipt',
            colorClass: 'text-warning bg-warning/10',
            progressClass: 'bg-warning',
          },
          {
            label: 'Being prepared',
            value: countStatuses('preparing', 'packed', 'packed_at_store'),
            icon: 'fa-solid fa-box',
            colorClass: 'text-info bg-info/10',
            progressClass: 'bg-info',
          },
          {
            label: 'Out for delivery',
            value: countStatuses(
              'assigned',
              'accepted',
              'picked_up',
              'out_for_delivery',
              'near_customer',
              'dispatched'
            ),
            icon: 'fa-solid fa-motorcycle',
            colorClass: 'text-primary bg-primary/10',
            progressClass: 'bg-primary',
          },
          {
            label: 'Delivered',
            value: countStatuses('delivered'),
            icon: 'fa-solid fa-circle-check',
            colorClass: 'text-success bg-success/10',
            progressClass: 'bg-success',
          },
        ]);

        this.topMetrics.set([
          {
            label: 'Orders Today',
            value: formatIndianCompactNumber(ordersToday.count || 0),
            hint: `Volume for ${this.selectedStoreName}`,
            badge: 'Live',
            badgeClass: 'badge-success badge-outline',
            icon: 'fa-solid fa-bag-shopping',
            iconClass: 'bg-primary/10 text-primary',
          },
          {
            label: 'Sales Today',
            value: formatIndianCompactCurrency(revenue),
            hint: `Revenue from ${this.selectedStoreName}`,
            badge: 'Live',
            badgeClass: 'badge-success badge-outline',
            icon: 'fa-solid fa-indian-rupee-sign',
            iconClass: 'bg-secondary/10 text-secondary',
          },
          {
            label: 'Riders Online',
            value: (riders.count || 0).toString(),
            hint: storeId
              ? `Fleet for ${this.selectedStoreName}`
              : 'Available delivery partners',
            badge: 'Active',
            badgeClass: 'badge-info badge-outline',
            icon: 'fa-solid fa-motorcycle',
            iconClass: 'bg-info/10 text-info',
          },
          {
            label: 'Users',
            value: formatIndianCompactNumber(users.total),
            hint: storeId
              ? `Customers of ${this.selectedStoreName}`
              : 'Total registered customers',
            badge: 'Total',
            badgeClass: 'badge-ghost',
            icon: 'fa-solid fa-users',
            iconClass: 'bg-accent/10 text-accent',
          },
        ]);

        this.recentOrders.set(recentOrders.data || []);

        // Compute chart data
        const chartOrders = chartOrdersResult.data || [];
        const dailyTotals = Array(7).fill(0);
        const dayLabels = Array(7).fill('');

        for (let i = 0; i < 7; i++) {
          const d = new Date(today);
          d.setDate(d.getDate() - (6 - i));
          dayLabels[i] = d.toLocaleDateString('en-IN', {
            timeZone: 'Asia/Kolkata',
            weekday: 'short',
          });
        }

        chartOrders.forEach((order) => {
          if (order.created_at && order.payable_amount) {
            const orderDate = new Date(order.created_at);
            orderDate.setHours(0, 0, 0, 0);
            const diffTime = today.getTime() - orderDate.getTime();
            const diffDays = Math.round(diffTime / (1000 * 3600 * 24));
            const idx = 6 - diffDays;
            if (idx >= 0 && idx < 7) {
              dailyTotals[idx] += Number(order.payable_amount);
            }
          }
        });

        this.weeklySales.set(
          dailyTotals.reduce((total, amount) => total + amount, 0)
        );
        this.averageOrderValue.set(
          orderData.length > 0 ? Math.round(revenue / orderData.length) : 0
        );

        this.chartData.set({ labels: dayLabels, values: dailyTotals });

        this.loading.set(false);
        this.lastUpdated.set(new Date());

        // Initialize chart after a short delay to ensure view is rendered
        setTimeout(() => this.initChart(), 100);
      },
      error: () => {
        this.error.set('Failed to sync with command center.');
        this.loading.set(false);
      },
    });
  }

  initChart(): void {
    if (!this.dashboardChart) return;

    if (this.chartInstance) {
      this.chartInstance.destroy();
    }

    const ctx = this.dashboardChart.nativeElement.getContext('2d');
    if (!ctx) return;

    const labels = this.chartData().labels;
    const values = this.chartData().values;

    this.chartInstance = new Chart(ctx, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Sales (₹)',
            data: values,
            borderColor: '#10b981', // Tailwind success/emerald
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            borderWidth: 3,
            tension: 0.4,
            fill: true,
            pointBackgroundColor: '#fff',
            pointBorderColor: '#10b981',
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(0,0,0,0.8)',
            titleFont: { size: 13, family: 'Inter' },
            bodyFont: { size: 14, weight: 'bold', family: 'Inter' },
            padding: 12,
            cornerRadius: 8,
            displayColors: false,
            callbacks: {
              label: function (context: any) {
                const val = context.parsed?.y ?? 0;
                return '₹' + val.toLocaleString('en-IN');
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: 'rgba(0,0,0,0.05)' },
            border: { display: false },
            ticks: {
              callback: function (value) {
                const amount = Number(value);
                return amount >= 1000
                  ? '₹' + (amount / 1000).toFixed(amount >= 10000 ? 0 : 1) + 'k'
                  : '₹' + amount;
              },
              font: { family: 'Inter', size: 11 },
              color: '#888',
            },
          },
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: {
              font: { family: 'Inter', size: 11 },
              color: '#888',
            },
          },
        },
        interaction: { intersect: false, mode: 'index' },
      },
    });
  }

  private setupRealtimeSubscription(): void {
    if (this.realtimeChannel) {
      supabase.removeChannel(this.realtimeChannel);
    }

    const currentStoreId = this.selectedStoreId();
    const orderFilter = currentStoreId
      ? { filter: `store_id=eq.${currentStoreId}` }
      : {};

    this.realtimeChannel = supabase
      .channel(`dashboard-updates:${currentStoreId ?? 'all'}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'orders', ...orderFilter },
        () => this.scheduleRealtimeRefresh()
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'rider_presence' },
        () => this.scheduleRealtimeRefresh()
      )
      .subscribe();
  }

  private scheduleRealtimeRefresh(): void {
    if (this.realtimeRefreshTimer) return;

    this.realtimeRefreshTimer = setTimeout(() => {
      this.realtimeRefreshTimer = null;
      this.loadDashboard();
    }, 1500);
  }

  private refreshRidersMetric(): void {
    const storeId = this.selectedStoreId();
    let query = supabase
      .from('rider_presence')
      .select('rider_id, rider:riders!inner(store_id)', { count: 'exact' })
      .eq('is_online', true);

    if (storeId) {
      query = query.eq('rider.store_id', storeId);
    }

    from(query).subscribe(({ count }) => {
      const metrics = [...this.topMetrics()];
      const riderMetric = metrics.find((m) => m.label === 'Riders Online');
      if (riderMetric) {
        riderMetric.value = (count || 0).toString();
        this.topMetrics.set(metrics);
      }
    });
  }

  getStatusClass(status: string): string {
    const s = status?.toUpperCase();
    switch (s) {
      case 'PENDING':
        return 'badge-warning';
      case 'PREPARING':
      case 'PACKED':
      case 'PACKED_AT_STORE':
        return 'badge-info';
      case 'ASSIGNED':
      case 'ACCEPTED':
        return 'badge-secondary';
      case 'PICKED_UP':
      case 'OUT_FOR_DELIVERY':
      case 'NEAR_CUSTOMER':
      case 'DISPATCHED':
        return 'badge-primary';
      case 'DELIVERED':
        return 'badge-success';
      case 'CANCELLED':
        return 'badge-error';
      default:
        return 'badge-ghost';
    }
  }

  private handleUpdateOrder(order: Record<string, any>): void {
    const currentOrders = [...this.recentOrders()];
    const index = currentOrders.findIndex((o) => o.id === order['id']);

    if (index !== -1) {
      // Preserve existing relations (user, store) if we only have the order update
      currentOrders[index] = {
        ...currentOrders[index],
        ...order,
        payable_amount: Number(order['payable_amount']),
      };
      this.recentOrders.set(currentOrders);
      this.lastUpdated.set(new Date());
    }
  }

  private handleNewOrder(order: Record<string, any>): void {
    // 1. Update KPI Metrics instantly (Optimistic)
    const metrics = [...this.topMetrics()];
    const ordersMetric = metrics.find((m) => m.label === 'Orders Today');
    if (ordersMetric) {
      const currentVal = parseIndianCompactNumber(ordersMetric.value);
      ordersMetric.value = formatIndianCompactNumber(currentVal + 1);
    }
    const salesMetric = metrics.find((m) => m.label === 'Sales Today');
    if (salesMetric) {
      const currentVal = parseIndianCompactNumber(salesMetric.value);
      const newVal = currentVal + (order['payable_amount'] || 0);
      salesMetric.value = formatIndianCompactCurrency(newVal);
    }
    this.topMetrics.set(metrics);
    this.lastUpdated.set(new Date());

    // 2. Fetch full relations for the feed item to keep it HQ
    from(
      supabase
        .from('orders')
        .select('*, user:users(name), store:stores(store_name)')
        .eq('id', order['id'])
        .single()
    ).subscribe(({ data }) => {
      if (!data) return;

      const currentOrders = [...this.recentOrders()];
      this.recentOrders.set(
        [data as DashboardOrder, ...currentOrders].slice(0, 6)
      );

      // 3. Update Chart
      const chartData = this.chartData();
      if (chartData.values.length > 0) {
        const values = [...chartData.values];
        values[6] += order['payable_amount'] || 0;
        this.chartData.set({ ...chartData, values });
        this.initChart();
      }
    });
  }
}
